import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import cn from "classnames";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import ClockIcon from "../../../../shared/icons/ClockIcon";
import CheckIcon from "../../../../shared/icons/CheckIcon";
import AppTooltip from "../../../../shared/AppTooltip/AppTooltip";
import style from "./MultiPointStatusCard.module.scss";
import { CopyCheckIcon, CopyIcon } from "lucide-react";

const MultiPointStatusCard = ({
  addresses,
  currentAddress,
  status,
  isLastPoint,
  isLoading,
  onStartLoading,
  onCompleteLoading,
  onStartUnloading,
  onCompleteUnloading,
  children,
  isActionDisabled = false,
}) => {
  const { t } = useTranslation();
  const copyTooltipId = `multi-point-copy-${useId().replace(/:/g, "")}`;
  const [copiedPoint, setCopiedPoint] = useState(null);

  useEffect(() => {
    if (!copiedPoint) return undefined;
    const timeout = setTimeout(() => setCopiedPoint(null), 2000);
    return () => clearTimeout(timeout);
  }, [copiedPoint]);

  const copyAddress = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPoint({ key, text });
    } catch {
      toast.error(t("orderInfo.ordersInfoAddressList.copyFailed"));
    }
  };

  const pointIndex = addresses.indexOf(currentAddress);
  const isProcessing = status === "processloadingcargo" || status === "processunloadingcargo";
  const isWaitingForLoading = status === "confirmed" || status === "headingtoloading";
  const isOnTheTrip = status === "onthetrip";

  if (pointIndex < 0 || (!isProcessing && !isWaitingForLoading && !isOnTheTrip)) return null;
  if (isOnTheTrip && !["Loading", "Unloading"].includes(currentAddress.type)) return null;

  const isLoadingCargo =
    status === "processloadingcargo" ||
    isWaitingForLoading ||
    (isOnTheTrip && currentAddress.type === "Loading");
  const operation = isLoadingCargo ? "Loading" : "Unloading";
  const phase = isProcessing ? "complete" : "start";
  const prefix = "orderInfoExpeditor.multiPointRoute";
  let buttonText;

  if (isLastPoint) {
    buttonText = t(`${prefix}.${phase}Final${operation}`);
  } else if (pointIndex === 0 && isLoadingCargo) {
    buttonText = isProcessing
      ? t(`${prefix}.nextPoint`)
      : t("orderInfo.orderDecision.startLoadingButton");
  } else {
    buttonText = t(`${prefix}.${phase}${operation}Point`, { point: pointIndex + 1 });
  }

  const onClick = isLoadingCargo
    ? isProcessing
      ? onCompleteLoading
      : onStartLoading
    : isProcessing
      ? onCompleteUnloading
      : onStartUnloading;

  const getPointStatus = (address, index) => {
    if (address.isCompleted) {
      return t(`${prefix}.${address.type === "Loading" ? "loadingCompleted" : "unloadingCompleted"}`);
    }
    if (index !== pointIndex) return t(`${prefix}.waitingArrival`);
    return t(`${prefix}.${isProcessing ? "processing" : "waiting"}${operation}`);
  };

  return (
    <section className={style.card}>
      <div className={style.header}>
        <h3 className={style.title}>{t(`${prefix}.title`)}</h3>
        <p className={style.subtitle}>
          {t(`${prefix}.currentPoint`, {
            point: pointIndex + 1,
            total: addresses.length,
            operation: t(`${prefix}.${isLoadingCargo ? "loading" : "unloading"}`),
          })}
        </p>
      </div>
      <ol className={style.points}>
        {addresses.map((address, index) => {
          const isActive = index === pointIndex && isProcessing;
          const addressText = [address.countryName, address.cityName, address.address].filter(Boolean).join(", ");
          const pointKey = address.id || index;
          const isCopied = copiedPoint?.key === pointKey && copiedPoint?.text === addressText;
          const copyLabel = t(`orderInfo.ordersInfoAddressList.${isCopied ? "addressCopied" : "copyAddress"}`);

          return (
            <li
              key={address.id || index}
              className={style.point}
              aria-current={index === pointIndex ? "step" : undefined}
            >
              <div className={style.marker} aria-hidden="true">
                <span
                  className={cn(style.icon, {
                    [style.iconActive]: isActive,
                    [style.iconCompleted]: address.isCompleted,
                  })}
                >
                  {address.isCompleted ? (
                    <CheckIcon width="20" height="20" />
                  ) : (
                    <ClockIcon
                      className={style.clockIcon}
                      fill={isActive ? "#fff" : "#6D7885"}
                      type={2}
                      width={24}
                      height={24}
                    />
                  )}
                </span>
              </div>
              <div className={style.addressCard}>
                <div className={style.addressContent}>
                  <p className={style.address} title={addressText || undefined}>
                    {addressText || "—"}
                  </p>
                  <p className={style.pointStatus}>{getPointStatus(address, index)}</p>
                </div>
                <button
                  type="button"
                  className={style.copyButton}
                  onClick={() => copyAddress(addressText, pointKey)}
                  disabled={!addressText}
                  aria-label={copyLabel}
                  data-tooltip-id={copyTooltipId}
                  data-tooltip-content={copyLabel}
                >
                  {isCopied ? <CopyCheckIcon size={20} aria-hidden="true" /> : <CopyIcon size={20} aria-hidden="true" />}
                </button>
              </div>
            </li>
          );
        })}
      </ol>
      <AppTooltip id={copyTooltipId} positionStrategy="fixed" delayShow={150} />
      {children}
      <div className={style.action}>
        <CustomButton
          type="button"
          text={buttonText}
          isNavy
          isFall
          onClick={onClick}
          isLoading={isLoading}
          disabled={isLoading || isActionDisabled}
        />
      </div>
    </section>
  );
};

export default MultiPointStatusCard;
